import { useId, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { parseClientId, readClientCaseFilter, writeClientCaseFilter } from '@/domain';
import { useDeletePatient, usePatient } from '@/application/usecases/useClients';
import { useSession } from '@/application/usecases/useSession';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { PatientIcon, PencilIcon } from '@/ui/icons';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { CaseInPlace } from '@/features/orders/CaseInPlace';
import { useConfirmAction } from '../shell/useConfirmAction';
import { PatientCases } from './ClientCases';
import { ClientLink } from './ClientLink';
import { statusOf } from './clientRefusal';
import { PatientFormSheet } from './PatientFormSheet';
import { useAddressParams } from './useAddressParams';
import {
  BackLink,
  ProfileFailed,
  ProfileLoading,
  ProfileNotFound,
  Stat,
  type BackTarget,
} from './ProfileParts';
import styles from './ClientProfile.module.css';

const BACK: BackTarget = { to: '/patients', label: strings.clients.backToPatients };

const notFound = (
  <ProfileNotFound
    back={BACK}
    icon={<PatientIcon />}
    heading={strings.clients.patientNotFound}
    detail={strings.clients.patientNotFoundDetail}
  />
);

/**
 * A patient's profile. The request of 6 October 2026 (second), item 4, as the
 * request of 6 October 2026 (third) changed it.
 *
 * The patient and their ID; every doctor they have been to, each a step from
 * the doctor's own profile, with the clinic and how many of the patient's
 * cases are with that doctor; and the patient's cases, each a step from the
 * case. One record whichever doctors the patient is seen by (item 3 of that
 * request): somebody seen at two clinics is one patient here, and the profile
 * says they have been to two doctors.
 *
 * A patient is a name and nothing else, so Rename is the one edit, offered to
 * a holder of `renameClients` (item 4). Delete, for a record no case names,
 * stays with `manageClients`.
 *
 * Since the request of 6 October 2026 (fifth): the patient's cases are the
 * table a doctor's are, searched, filtered, ordered and paged by the server
 * with its state in the page's address, and a case opens here, over the page,
 * instead of leaving for Orders.
 */
export function PatientProfileScreen() {
  const { id: param } = useParams<{ id: string }>();
  const id = parseClientId(param);
  // Keyed by the record: another patient's profile starts on its own first page of cases.
  return id === null ? notFound : <PatientProfile key={id} id={id} />;
}

function PatientProfile({ id }: { id: string }) {
  const navigate = useNavigate();
  const permissions = useSession().data?.permissions ?? [];
  const mayManage = permissions.includes('manageClients');
  const mayRename = permissions.includes('renameClients');
  const patient = usePatient(id);
  const remove = useDeletePatient();
  const ask = useConfirmAction();
  const ids = { name: useId(), doctors: useId(), cases: useId() };
  const [renaming, setRenaming] = useState(false);
  const [, replace] = useAddressParams();

  if (patient.isPending) return <ProfileLoading back={BACK} label={strings.clients.loadingPatient} />;
  // Gone, whatever copy is still in hand: deleted since it was read, or never there.
  if (statusOf(patient.error) === 404) return notFound;
  // Never read. A copy that is in hand stays on screen through a refresh that failed.
  if (patient.data === undefined) {
    return <ProfileFailed back={BACK} error={patient.error} onRetry={() => void patient.refetch()} />;
  }

  const record = patient.data;
  // Only a record with no case can go: the server refuses any other.
  const mayDelete = mayManage && record.caseCount === 0;

  /**
   * A figure pressed: the cases, all of them or the active only, or the
   * doctors, each brought into view. The lab's request of 17 October 2026, item 1.
   */
  function showCases(active: boolean) {
    replace((current) =>
      writeClientCaseFilter(new URLSearchParams(current), {
        sort: readClientCaseFilter(new URLSearchParams(current)).sort,
        ...(active ? { status: 'active' as const } : {}),
      }),
    );
    document.getElementById(ids.cases)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }

  function showDoctors() {
    document.getElementById(ids.doctors)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }

  function deleteRecord() {
    ask(
      strings.clients.confirmDeletePatient(record.name),
      () =>
        remove.mutateAsync(record.id).then(
          () => {
            // At once: the record is gone, and a profile left on screen would ask for it again.
            void navigate(BACK.to, { replace: true });
            toast(strings.clients.patientDeleted(record.name), { tone: 'success' });
          },
          (error: unknown) => {
            // A case booked in meanwhile is why: the record is read again, and Delete goes.
            void patient.refetch();
            throw error;
          },
        ),
      { title: strings.clients.deletePatientTitle, confirmLabel: strings.clients.deleteRecord },
    );
  }

  return (
    <div>
      <BackLink {...BACK} />

      <section className={styles.card} aria-labelledby={ids.name}>
        <div className={styles.top}>
          <div className={styles.who}>
            <span className={styles.lbl}>{strings.clients.patientLabel}</span>
            {/* A name may be in Arabic: it sets its own direction, as on the case screen. */}
            <h2 id={ids.name} dir="auto">
              {record.name}
            </h2>
            {/* The ID: never typed, never used twice. The request of 6 October 2026 (third), item 2. */}
            <p className={styles.code}>{record.code}</p>
          </div>
          {(mayRename || mayDelete) && (
            <div className={styles.actions}>
              {mayRename && (
                <Button icon={<PencilIcon />} onClick={() => setRenaming(true)}>
                  {strings.clients.rename}
                </Button>
              )}
              {mayDelete && (
                <Button disabled={remove.isPending} onClick={deleteRecord}>
                  {strings.clients.deleteRecord}
                </Button>
              )}
            </div>
          )}
        </div>

        <dl className={styles.facts}>
          <div>
            <dt>{strings.clients.latestCase}</dt>
            <dd>
              {record.lastCaseAt === null ? strings.clients.noCases : formatDay(record.lastCaseAt)}
            </dd>
          </div>
          <div>
            <dt>{strings.clients.addedOn}</dt>
            <dd>{formatDay(record.createdAt)}</dd>
          </div>
        </dl>

        <div className={styles.stats}>
          <Stat label={strings.clients.statCases} value={record.caseCount} onPress={() => showCases(false)} />
          <Stat label={strings.clients.statActive} value={record.activeCaseCount} onPress={() => showCases(true)} />
          <Stat label={strings.clients.doctorsHeading} value={record.doctorCount} onPress={showDoctors} />
        </div>
      </section>

      {/*
        Every doctor the patient has been to, the one with the most of their
        cases first, as the record carries them. Each name opens the doctor's
        profile for a reader who holds that screen, and is the plain name for
        anybody else.
      */}
      <section className={styles.card} aria-labelledby={ids.doctors}>
        <div className={styles.sectionHead}>
          <h3 id={ids.doctors}>
            {strings.clients.doctorsHeading}
            <span>{record.doctorCount.toLocaleString()}</span>
          </h3>
        </div>

        {record.doctors.length === 0 ? (
          <p className={styles.hint}>{strings.clients.noDoctorsOfPatient}</p>
        ) : (
          <>
            {/* One patient, and it says so: the same person at two clinics is not two records. */}
            {record.doctors.length > 1 && (
              <p className={styles.note}>{strings.clients.seenBy(record.doctors.length)}</p>
            )}
            <ul className={styles.list}>
              {record.doctors.map((doctor) => (
                <li key={doctor.id} className={styles.item}>
                  <span className={styles.rowText}>
                    <b dir="auto">
                      <ClientLink kind="doctor" id={doctor.id} name={doctor.name} />
                    </b>
                    <span>
                      <span className={styles.id}>{doctor.code}</span>
                      {doctor.clinic !== null && ` · ${doctor.clinic}`}
                    </span>
                  </span>
                  {/* The patient's cases with this doctor. */}
                  <span className={styles.rowCount}>
                    {strings.clients.casesCounted(doctor.caseCount)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <div id={ids.cases}>
        <PatientCases patient={record} />
      </div>

      {/* The case a row opened, over this page: a drawer, or on a phone a page of its own. */}
      <CaseInPlace returnLabel={record.name} />

      {renaming && (
        <PatientFormSheet
          patient={record}
          onClose={() => setRenaming(false)}
          onDone={() => setRenaming(false)}
          onStale={() => void patient.refetch()}
        />
      )}
    </div>
  );
}
